var $7b9311149c24d2ca$exports = {};
// Arrow keys move focus between items; Elm owns what is open and what is
// chosen.
const $7b9311149c24d2ca$var$TRIGGER = '.dropdown__trigger';
const $7b9311149c24d2ca$var$ITEM = '.dropdown__item';
const $7b9311149c24d2ca$var$FOCUSABLE = '.dropdown__item--focusable';
// A checkbox or radio takes focus but has no cursor Home and End belong to.
const $7b9311149c24d2ca$var$TEXT_FIELD = 'input:not([type=checkbox]):not([type=radio]), textarea';
// A submenu is a li: it opens by a class changing, closes by an event we send.
const $7b9311149c24d2ca$var$INNER = '.dropdown__item--inner';
const $7b9311149c24d2ca$var$OPEN_INNER = `${$7b9311149c24d2ca$var$INNER}[open]`;
class $7b9311149c24d2ca$var$ElmDropdown extends HTMLElement {
    static observedAttributes = [
        'open'
    ];
    connectedCallback() {
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);
        this.submenus = new MutationObserver((records)=>this.onSubmenuToggled(records));
        this.submenus.observe(this, {
            attributes: true,
            attributeFilter: [
                'class'
            ],
            subtree: true
        });
        // Capture: Item.onKeyDown stops the event on the li.
        this.addEventListener('keydown', this.onKeyDown, true);
        this.addEventListener('focusout', this.onFocusOut);
    }
    disconnectedCallback() {
        this.submenus.disconnect();
        this.removeEventListener('keydown', this.onKeyDown, true);
        this.removeEventListener('focusout', this.onFocusOut);
    }
    attributeChangedCallback(_name, before, after) {
        if (before === after) return;
        if (after === null) this.returnFocus();
        else requestAnimationFrame(()=>this.focusFirst());
    }
    // Ours, not a submenu's: the nearest .dropdown separates them.
    own(selector, scope = this) {
        return [
            ...scope.querySelectorAll(selector)
        ].filter((node)=>node.parentElement.closest('.dropdown') === scope);
    }
    trigger(scope = this) {
        return this.own($7b9311149c24d2ca$var$TRIGGER, scope)[0] ?? null;
    }
    // The dropdown a node sits in — this one, or an open submenu of ours. A
    // closed submenu is just another of our items, so Escape there is ours.
    scopeOf(node) {
        const scope = node?.closest($7b9311149c24d2ca$var$OPEN_INNER);
        return scope && this.contains(scope) ? scope : this;
    }
    openInners() {
        return this.own($7b9311149c24d2ca$var$OPEN_INNER);
    }
    // Filters hide items rather than removing them.
    items(scope = this) {
        return this.own($7b9311149c24d2ca$var$FOCUSABLE, scope).filter((item)=>item.checkVisibility());
    }
    // Elm opens and closes the submenu; focus has to follow it both ways, or it
    // stays on an item that is now display:none and the browser drops it.
    onSubmenuToggled(records) {
        for (const { target: target } of records){
            if (!target.matches?.($7b9311149c24d2ca$var$INNER) || !this.contains(document.activeElement)) continue;
            if (target.classList.contains('dropdown--open')) this.focusInto(target);
            else if (target.contains(document.activeElement)) this.own($7b9311149c24d2ca$var$TRIGGER, target)[0]?.focus();
        }
    }
    focusInto(scope) {
        const [first] = this.items(scope);
        (first?.querySelector($7b9311149c24d2ca$var$TEXT_FIELD) ?? first)?.focus();
    }
    // A combobox opens on its own input, so taking focus would stop the typing.
    // The first item, not the chosen one: a filtering menu puts its field first.
    focusFirst() {
        if (document.activeElement?.matches($7b9311149c24d2ca$var$TEXT_FIELD)) return;
        this.focusInto(this);
    }
    // A blur is a close too, and focus has already gone where the user sent it.
    returnFocus() {
        if (this.contains(document.activeElement)) this.trigger()?.focus();
    }
    // Elm is told the dropdown closed rather than asked which one did: the event
    // arrives on the element or the inner menu's li, so there is no id to carry
    // and none to mismatch.
    requestClose(scope = this) {
        if (scope === this && !this.hasAttribute('open')) return;
        scope.dispatchEvent(new CustomEvent('close', {
            bubbles: false
        }));
    }
    // Focus leaving closes what it left. Moving from an inner menu back out to
    // our own items closes that menu and no more; leaving the element entirely
    // closes every menu that was open inside it.
    onFocusOut(event) {
        const to = event.relatedTarget;
        const staying = to && this.contains(to);
        for (const inner of this.openInners())if (!staying || !inner.contains(to)) this.requestClose(inner);
        if (!staying) this.requestClose();
    }
    onKeyDown(event) {
        // A closed dropdown answers nothing: Escape here belongs to whatever
        // encloses it, usually the modal the form is in.
        if (!this.hasAttribute('open')) return;
        // Capture reaches the outermost first; the innermost claims its keys.
        if (event.target.closest('elm-dropdown') !== this) return;
        if (event.key === 'Escape') {
            // Only the innermost open dropdown answers, and stopping here keeps it
            // from closing every ancestor too.
            const scope = this.scopeOf(event.target);
            event.preventDefault();
            event.stopPropagation();
            // Focus first, close second: a combobox trigger reopens itself on focus,
            // and Elm renders once both messages are in, so Closed has to be last.
            (scope === this ? this.trigger() : scope)?.focus();
            this.requestClose(scope);
            return;
        }
        const step = {
            ArrowDown: 1,
            ArrowUp: -1
        }[event.key];
        // Home and End belong to the cursor in a text field.
        const jump = event.target.matches($7b9311149c24d2ca$var$TEXT_FIELD) ? undefined : ({
            Home: 0,
            End: -1
        })[event.key];
        // An open submenu is walked on its own: its items belong to a li.dropdown,
        // and Elm moves between the two menus with the left and right arrows.
        const scope = event.target.closest($7b9311149c24d2ca$var$ITEM)?.parentElement.closest('.dropdown') ?? this;
        const items = this.items(scope);
        if (!items.length) return;
        let next;
        if (step) {
            const here = items.indexOf(event.target.closest($7b9311149c24d2ca$var$ITEM));
            next = here === -1 ? items[0] : items[(here + step + items.length) % items.length];
        } else if (jump !== undefined) next = items.at(jump);
        else return;
        // Otherwise the page scrolls under the open menu.
        event.preventDefault();
        next.focus();
        // A menu tall enough to scroll does not follow focus on its own when the
        // walk wraps from one end to the other.
        next.scrollIntoView?.({
            block: 'nearest'
        });
    }
}
if (!customElements.get('elm-dropdown')) customElements.define('elm-dropdown', $7b9311149c24d2ca$var$ElmDropdown);


//# sourceMappingURL=project.c58d5ecc.js.map
